import * as XLSX from "xlsx";
import { v4 as uuidv4 } from "uuid";
import { FileModel } from "../dto/create-file-input";
import { AttachmentTypes } from "../types/AttachmentTypes";

export class UtilService {
  static exportToExcel(data: any[], name: string = "data") {
    const exportToExcel = () => {
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, `${name}`);
      XLSX.writeFile(workbook, `${name}.xlsx`);
    };
    exportToExcel();
  }

  static uuid(): string {
    return uuidv4();
  }

  static delay(mills?: number) {
    return new Promise((resolve) => setTimeout(resolve, mills ?? 3000));
  }

  static titleCase(str: string) {
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  static dateFormat(dateStr: string, format: string = "DD-MM-YYYY"): string {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return dateStr;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    switch (format) {
      case "YYYY-MM-DD":
        return `${year}-${month}-${day}`;
      case "DD-MM-YYYY":
        return `${day}-${month}-${year}`;
      case "MM-DD-YYYY":
        return `${month}-${day}-${year}`;
      default:
        return dateStr;
    }
  }

  static toLocalDate(date: string) {
    return new Date(date).toLocaleString();
  }

  static imageToBase64 = async (
    file: File,
    setError?: React.Dispatch<React.SetStateAction<string>>
  ) => {
    try {
      const reader = new FileReader();
      return new Promise<FileModel>((resolve, reject) => {
        reader.onload = () => {
          const base64String = reader.result?.toString().split(",")[1];
          if (base64String) {
            let fileModel = new FileModel();
            fileModel.filename = file.name;
            fileModel.mimetype = file.type;
            fileModel.file = base64String;
            resolve(fileModel);
          } else {
            reject("Failed to convert file to base64");
          }
        };
        reader.onerror = () => reject("Error reading file");
        reader.readAsDataURL(file);
      });
    } catch (error) {
      if (setError) {
        setError("Error processing file");
      }
      return undefined;
    }
  };

  static base64ToAttachment(base64: string, mimetype: string) {
    try {
      const isPdfFile = mimetype?.toLowerCase().includes("pdf");
      if (isPdfFile) {
        return `data:application/pdf;base64,${base64}`;
      } else if (mimetype.startsWith("image/")) {
        return `data:${mimetype};base64,${base64}`;
      } else {
        return `data:${mimetype};base64,${base64}`;
      }
    } catch (error) {
      return "";
    }
  }

  static attachmentsToFileModel(attachments?: any[]): FileModel[] | null {
    if (!Array.isArray(attachments) || attachments.length === 0) {
      return null;
    }

    return attachments
      .filter(
        (attachment) =>
          attachment?.file !== undefined &&
          attachment?.extension !== undefined &&
          attachment?.name !== undefined
      )
      .map((attachment) => ({
        file: attachment.file,
        mimetype: attachment.extension,
        filename: attachment.name,
      }));
  }

  static async onAttachFile(
    file: File,
    setError: React.Dispatch<React.SetStateAction<string>>,
    supportedFileTypes?: string[]
  ) {
    if (!file) {
      setError("No file selected");
      return;
    }
    const validTypes = supportedFileTypes ?? AttachmentTypes;
    if (!validTypes.includes(file.type)) {
      setError("Only JPG, PNG, WebP, PDF, DOCX files are allowed");
      return;
    }
    try {
      return await this.imageToBase64(file, setError);
    } catch (error) {
      setError("Invalid file. Please try again.");
    }
  }
}
